import { useEffect, useRef, useState } from 'react'

import { usePageVisible } from '@/hooks'
import { isSameBox, measureTourTarget } from './tourBox'

// The Transactions tab shows a skeleton first, so an area gets this long to appear.
const WAIT_MILLISECONDS = 5000

// Measured every frame, so the box follows scrolling, resizing, layout shifts and an area that is
// swapped for a fresh copy (a skeleton turning into the table).
export const useTourTarget = (target: string) => {
  const [box, setBox] = useState<DOMRect | null>(null)
  const [isMissing, setIsMissing] = useState(false)
  const hasFound = useRef(false)
  const isPageVisible = usePageVisible()

  useEffect(() => {
    let frame = 0
    const measure = () => {
      const next = measureTourTarget(target)
      if (next) hasFound.current = true
      setBox((previous) => (isSameBox(previous, next) ? previous : next))
      frame = requestAnimationFrame(measure)
    }
    measure()
    return () => cancelAnimationFrame(frame)
  }, [target])

  // A hidden page draws no frames, so nothing can be found there. The wait only counts while the
  // page is visible, and starts over each time the user comes back.
  useEffect(() => {
    if (!isPageVisible) return
    const timer = window.setTimeout(() => {
      if (!hasFound.current) setIsMissing(true)
    }, WAIT_MILLISECONDS)
    return () => window.clearTimeout(timer)
  }, [isPageVisible])

  return { box, isMissing }
}
